import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, MessageCircle, Phone, Instagram, MapPin } from "lucide-react";
import { useLang } from "@/i18n";

const HERO_BG = "/assets/hero-bg.jpg";

const WHATSAPP_URL =
  "https://wa.me/9647760506057?text=%D9%85%D8%B1%D8%AD%D8%A8%D9%8B%D8%A7%20%D9%85%D8%B1%D9%83%D8%B2%20%D9%83%D9%88%D9%86%D9%83%D8%AA%D8%AF%D8%8C%20%D8%A3%D8%B1%D8%BA%D8%A8%20%D8%A8%D9%85%D8%B9%D8%B1%D9%81%D8%A9%20%D8%AE%D9%8A%D8%A7%D8%B1%D8%A7%D8%AA%20%D8%AA%D8%BA%D9%84%D9%8A%D9%81%20%D9%88%D8%AD%D9%85%D8%A7%D9%8A%D8%A9%20%D8%B3%D9%8A%D8%A7%D8%B1%D8%AA%D9%8A";

const lineReveal = (delay) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Hero() {
  const { t, lang } = useLang();
  const ArrowIcon = lang === "ar" ? ArrowLeft : ArrowRight;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={HERO_BG} alt={t.hero.imgAlt} className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303]/85 via-[#030303]/45 to-[#030303]" />
      <div
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,9,8,0.5)_100%)]"
        aria-hidden="true"
      />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex min-h-[100svh] max-w-5xl flex-col items-center justify-center px-5 pb-20 pt-28 text-center"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mb-7 overflow-hidden rounded-full shadow-2xl shadow-black/60 ring-1 ring-[#E3AF4F]/50"
        >
          <img src="/assets/cc-logo.png" alt="" className="h-20 w-20 object-cover sm:h-24 sm:w-24" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-[11px] font-semibold tracking-[0.5em] text-[#E3AF4F] sm:text-xs"
        >
          CONNECTED CENTER
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-4 flex items-center gap-3"
          aria-hidden="true"
        >
          <span className="h-px w-10 bg-[#E3AF4F]/60" />
          <span className="h-1.5 w-1.5 rotate-45 bg-[#E3AF4F]" />
          <span className="h-px w-10 bg-[#E3AF4F]/60" />
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="mt-4 text-sm font-medium text-[#A8A29E]"
        >
          {t.hero.kicker}
        </motion.p>

        <h1 className="mt-9 font-heading text-4xl font-extrabold leading-[1.3] text-[#F5F2EB] sm:text-5xl lg:text-6xl">
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" {...lineReveal(0.55)}>
              {t.hero.title1a} <span className="text-gold-grad">{t.hero.title1b}</span>
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span className="block" {...lineReveal(0.7)}>
              {t.hero.title2a} <span className="text-gold-grad">{t.hero.title2b}</span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-[#CFC9BE] sm:text-lg"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 1.0 }}
          className="mt-8 flex items-center gap-4 text-[10px] font-bold tracking-[0.35em] text-[#F5F2EB]/70 sm:text-xs"
        >
          <span>PPF</span>
          <span className="h-3 w-px bg-[#E3AF4F]/70" />
          <span>COLOR WRAP</span>
          <span className="h-3 w-px bg-[#E3AF4F]/70" />
          <span>DETAILING</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            href="#works"
            data-testid="hero-explore-works-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#E3AF4F] px-8 py-3.5 text-sm font-bold text-[#000000] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#F5D08C] hover:shadow-lg hover:shadow-[#E3AF4F]/25"
          >
            {t.hero.btnWorks}
            <ArrowIcon
              size={16}
              className={`transition-transform duration-300 ${
                lang === "ar" ? "group-hover:-translate-x-1" : "group-hover:translate-x-1"
              }`}
            />
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#F5F2EB]/25 px-8 py-3.5 text-sm font-bold text-[#F5F2EB] backdrop-blur transition-all duration-300 hover:border-[#E3AF4F]/60 hover:bg-[#F5F2EB]/5"
          >
            <MessageCircle size={16} className="text-[#E3AF4F]" />
            {t.hero.btnWhats}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.25 }}
          className="mt-14 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-xs text-[#A8A29E]"
        >
          <a
            href="tel:+9647760506057"
            data-testid="hero-phone-link"
            dir="ltr"
            className="flex items-center gap-2 transition-colors duration-300 hover:text-[#E3AF4F]"
          >
            <Phone size={13} className="text-[#E3AF4F]" />
            +964 776 050 6057
          </a>
          <span className="hidden h-3 w-px bg-[#F5F2EB]/20 sm:block" aria-hidden="true" />
          <a
            href="https://instagram.com/connected_center_basra"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-instagram-link"
            dir="ltr"
            className="flex items-center gap-2 transition-colors duration-300 hover:text-[#E3AF4F]"
          >
            <Instagram size={13} className="text-[#E3AF4F]" />
            @connected_center_basra
          </a>
          <span className="hidden h-3 w-px bg-[#F5F2EB]/20 sm:block" aria-hidden="true" />
          <span className="flex items-center gap-2">
            <MapPin size={13} className="text-[#E3AF4F]" />
            {t.contact.info[0].value}
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
}
